import { useCallback, useEffect, useState } from 'react';

import {
  FilterWrapper,
  FilterWrapperVariantType
} from '@screens/Category/ui/Filter/FilterModal/FilterWrapper/FilterWrapper';
import { RadioListItem, RadioListItemType } from '@screens/Category/ui/Filter/RadioListItem/RadioListItem';
import { getRanges } from '@screens/Category/utils/getRange/getRange';
import { Metrics } from '@shared/types/enums/metrics.enum';
import { UnitType } from '@shared/types/product';
import { Input } from '@shared/ui/Input';
import { HStack, VStack } from '@shared/ui/Stack';
import { sendMetrics } from '@shared/utils/sendMetrics/sendMetrics';

import cls from './InputRange.module.scss';

type InputRangeProps = {
  label: string;
  onChange: (min: string, max: string, filterId: string) => void;
  baseMinValue: string;
  baseMaxValue: string;
  filterId: string;
  unit?: UnitType | undefined;
  activeFilters: Record<string, number[]>;
  listType?: FilterWrapperVariantType;
};

export const InputRange = ({
  label,
  onChange,
  filterId,
  unit,
  baseMinValue,
  baseMaxValue,
  activeFilters,
  listType
}: InputRangeProps) => {
  const [min, setMin] = useState<string>(
    activeFilters[filterId] && activeFilters[filterId].length > 1 ? activeFilters[filterId][0].toString() : ''
  );
  const [max, setMax] = useState<string>(
    activeFilters[filterId] && activeFilters[filterId].length > 1 ? activeFilters[filterId][1].toString() : ''
  );

  const [radioList, setRadioList] = useState<RadioListItemType[]>(
    getRanges(Number(baseMinValue), Number(baseMaxValue), activeFilters[filterId] as number[])
  );

  const filterLabel = `${label} ${unit?.shortName ? `(${unit.shortName})` : ''}`;
  const isActiveFilter = min !== '' || max !== '' || radioList.find((el) => el.active) !== undefined;

  const onChangeMinValue = (value: string) => {
    setMin(value.replace(/[^0-9.,]/g, ''));
    onChange(value.replace(/[^0-9.,]/g, ''), max, filterId);
    setRadioList(radioList.map((el) => ({ ...el, active: false })));
    sendMetrics(Metrics.FILTER_GOAL);
  };

  const onChangeMaxValue = (value: string) => {
    setMax(value.replace(/[^0-9.,]/g, ''));
    onChange(min, value.replace(/[^0-9.,]/g, ''), filterId);
    setRadioList(radioList.map((el) => ({ ...el, active: false })));
    sendMetrics(Metrics.FILTER_GOAL);
  };

  const resetList = () => {
    setMin('');
    setMax('');
    setRadioList(radioList.map((el) => ({ ...el, active: false })));
    onChange('', '', filterId);
  };

  const onChangeHandler = useCallback(
    (value: RadioListItemType) => {
      const newValue = radioList.map((el) =>
        el.id === value.id
          ? { ...el, active: true }
          : {
              ...el,
              active: false
            }
      );
      setRadioList(newValue);
      onChange(value.min.toString(), value.max.toString(), filterId);
      sendMetrics(Metrics.FILTER_GOAL);
    },
    [filterId, onChange, radioList]
  );

  useEffect(() => {
    if (!activeFilters[filterId]) {
      setMin('');
      setMax('');
    }
  }, [filterId, activeFilters]);

  return (
    <FilterWrapper
      variant={listType || 'range'}
      className={cls.wrap}
      label={filterLabel}
      isActiveFilter={isActiveFilter}
      resetActiveFilter={resetList}
    >
      <HStack gap={'12'}>
        <div className={cls.field}>
          <Input
            testId={'input-range-min'}
            size={'s'}
            label={`от ${baseMinValue}`}
            className={cls.input}
            value={min}
            onChange={onChangeMinValue}
          />
        </div>
        <div className={cls.field}>
          <Input
            testId={'input-range-max'}
            size={'s'}
            label={`до ${baseMaxValue}`}
            value={max}
            className={cls.input}
            onChange={onChangeMaxValue}
          />
        </div>
      </HStack>

      {listType === 'radio-list' && radioList && radioList.length ? (
        <VStack className={cls.list}>
          {radioList.map((el, index) => {
            return (
              <RadioListItem
                key={index}
                onChange={onChangeHandler}
                label={el.min === Number(baseMinValue) ? `до ${el.max} см` : `от ${el.min} см - до ${el.max} см`}
                value={el}
              />
            );
          })}
        </VStack>
      ) : null}
    </FilterWrapper>
  );
};
